/**
 * BatchReceiptForm Component
 *
 * User Story: US-P6-001 - Inventory Management
 * Step 3: Batch Receipt (Shipment Entry)
 *
 * Form for recording new material shipments.
 * Creates an inventory batch with auto-generated batch code.
 */

import { useState, useEffect, useMemo } from 'react'
import { toLocalDateString } from '../../utils/dateUtils'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Card, CardContent } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Separator } from '@/components/ui/separator'
import { Checkbox } from '@/components/ui/checkbox'
import { Package, Calculator, Calendar, AlertCircle, Loader2, Plus } from 'lucide-react'
import { useInventoryItems } from '@/hooks/useInventoryItems'
import { useSuppliers } from '@/hooks/useSuppliers'
import SupplierDialog from './SupplierDialog'
import InventoryItemDialog from './InventoryItemDialog'

/**
 * Format a number as currency (GBP)
 */
function formatCurrency(value) {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

/**
 * Format a number with up to 4 decimal places
 */
function formatNumber(value, decimals = 2) {
  return new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  }).format(value)
}

/**
 * Generate a preview batch code
 * Format: {PREFIX}-{YYMMDD}-{SEQ}
 */
function generateBatchCodePreview(prefix, date) {
  if (!prefix || !date) return ''
  const dateStr = toLocalDateString(typeof date === 'string' ? new Date(date.includes('T') ? date : date + 'T00:00:00') : date).slice(2).replace(/-/g, '')
  return `${prefix}-${dateStr}-A`
}

/**
 * BatchReceiptForm Component
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onOpenChange - Callback when open state changes
 * @param {Function} props.onSubmit - Callback when form is submitted (receives shipment data)
 * @param {boolean} props.isSubmitting - Whether the form is being submitted
 * @param {Object} props.defaultItem - Pre-selected inventory item (optional)
 */
export default function BatchReceiptForm({
  open,
  onOpenChange,
  onSubmit,
  isSubmitting = false,
  defaultItem = null,
}) {
  // Fetch inventory items and suppliers
  const { items: inventoryItems, isLoading: itemsLoading, refetch: refetchItems } = useInventoryItems({ activeOnly: true })
  const { suppliers, isLoading: suppliersLoading, refetch: refetchSuppliers } = useSuppliers({ activeOnly: true })

  // State for inline creation dialogs
  const [supplierDialogOpen, setSupplierDialogOpen] = useState(false)
  const [itemDialogOpen, setItemDialogOpen] = useState(false)
  const [pendingSupplierSelect, setPendingSupplierSelect] = useState(false)
  const [pendingItemSelect, setPendingItemSelect] = useState(false)

  // Form state
  const [formData, setFormData] = useState({
    item_id: defaultItem?.id || '',
    supplier_id: '',
    purchase_date: toLocalDateString(new Date()),
    quantity: '',
    use_quantity_override: false,
    quantity_override: '',
    product_cost: '',
    delivery_cost: '',
    vat_amount: '',
    invoice_reference: '',
    notes: '',
  })
  const [error, setError] = useState(null)

  // Reset form when dialog opens/closes or default item changes
  useEffect(() => {
    if (open) {
      setFormData({
        item_id: defaultItem?.id || '',
        supplier_id: defaultItem?.preferred_supplier_id || '',
        purchase_date: toLocalDateString(new Date()),
        quantity: '',
        use_quantity_override: false,
        quantity_override: '',
        product_cost: '',
        delivery_cost: '',
        vat_amount: '',
        invoice_reference: '',
        notes: '',
      })
      setError(null)
    }
  }, [open, defaultItem])

  // Get the selected item details
  const selectedItem = useMemo(() => {
    if (!formData.item_id) return null
    return inventoryItems.find(item => item.id === formData.item_id) || null
  }, [formData.item_id, inventoryItems])

  // Update supplier when item changes (use preferred supplier)
  useEffect(() => {
    if (selectedItem?.preferred_supplier_id && !formData.supplier_id) {
      setFormData(prev => ({
        ...prev,
        supplier_id: selectedItem.preferred_supplier_id
      }))
    }
  }, [selectedItem])

  // Calculate quantities and costs
  const calculations = useMemo(() => {
    if (!selectedItem || !formData.quantity) {
      return {
        calculatedQuantity: 0,
        effectiveQuantity: 0,
        productCost: 0,
        deliveryCost: 0,
        vatAmount: 0,
        landedCost: 0,
        landedCostExVat: 0,
        unitCost: 0,
        unitCostExVat: 0,
        quantityOverridden: false
      }
    }

    const quantity = parseFloat(formData.quantity) || 0
    const conversionFactor = selectedItem.conversion_factor || 1
    const calculatedQuantity = quantity * conversionFactor

    // Use override if enabled and valid, otherwise use calculated
    const quantityOverride = formData.use_quantity_override ? parseFloat(formData.quantity_override) || 0 : 0
    const effectiveQuantity = formData.use_quantity_override && quantityOverride > 0
      ? quantityOverride
      : calculatedQuantity

    const productCost = parseFloat(formData.product_cost) || 0
    const deliveryCost = parseFloat(formData.delivery_cost) || 0
    const vatAmount = parseFloat(formData.vat_amount) || 0
    const landedCost = productCost + deliveryCost
    const landedCostExVat = landedCost - vatAmount
    const unitCost = effectiveQuantity > 0 ? landedCost / effectiveQuantity : 0
    const unitCostExVat = effectiveQuantity > 0 ? landedCostExVat / effectiveQuantity : 0

    return {
      calculatedQuantity,
      effectiveQuantity,
      productCost,
      deliveryCost,
      vatAmount,
      landedCost,
      landedCostExVat,
      unitCost,
      unitCostExVat,
      quantityOverridden: formData.use_quantity_override && quantityOverride > 0
    }
  }, [selectedItem, formData.quantity, formData.use_quantity_override, formData.quantity_override, formData.product_cost, formData.delivery_cost, formData.vat_amount])

  // Generate batch code preview
  const batchCodePreview = useMemo(() => {
    if (!selectedItem) return ''
    return generateBatchCodePreview(selectedItem.batch_prefix, formData.purchase_date)
  }, [selectedItem, formData.purchase_date])

  // Handle form field changes
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    setError(null)
  }

  // Handle new supplier creation
  const handleSupplierCreated = () => {
    setSupplierDialogOpen(false)
    setPendingSupplierSelect(true)
    refetchSuppliers()
  }

  // Handle new item creation
  const handleItemCreated = () => {
    setItemDialogOpen(false)
    setPendingItemSelect(true)
    refetchItems()
  }

  // Auto-select newest supplier after creation
  useEffect(() => {
    if (pendingSupplierSelect && suppliers.length > 0) {
      const sorted = [...suppliers].sort((a, b) =>
        new Date(b.created_at) - new Date(a.created_at)
      )
      handleChange('supplier_id', sorted[0].id)
      setPendingSupplierSelect(false)
    }
  }, [suppliers, pendingSupplierSelect])

  // Auto-select newest item after creation
  useEffect(() => {
    if (pendingItemSelect && inventoryItems.length > 0) {
      const sorted = [...inventoryItems].sort((a, b) =>
        new Date(b.created_at) - new Date(a.created_at)
      )
      handleChange('item_id', sorted[0].id)
      setPendingItemSelect(false)
    }
  }, [inventoryItems, pendingItemSelect])

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)

    // Validate required fields
    if (!formData.item_id) {
      setError('Please select an item')
      return
    }

    if (!formData.quantity || parseFloat(formData.quantity) <= 0) {
      setError('Quantity must be greater than 0')
      return
    }

    if (formData.use_quantity_override && (!formData.quantity_override || parseFloat(formData.quantity_override) <= 0)) {
      setError('Quantity override must be greater than 0')
      return
    }

    if (formData.product_cost === '' || parseFloat(formData.product_cost) < 0) {
      setError('Product cost is required')
      return
    }

    try {
      const submitData = {
        item_id: formData.item_id,
        supplier_id: formData.supplier_id || null,
        purchase_date: formData.purchase_date,
        quantity: parseFloat(formData.quantity),
        product_cost: parseFloat(formData.product_cost),
        delivery_cost: parseFloat(formData.delivery_cost) || 0,
        vat_amount: parseFloat(formData.vat_amount) || 0,
        invoice_reference: formData.invoice_reference.trim() || null,
        notes: formData.notes.trim() || null,
      }

      // Add quantity override if enabled
      if (formData.use_quantity_override && formData.quantity_override) {
        submitData.quantity_override = parseFloat(formData.quantity_override)
      }

      await onSubmit(submitData)
    } catch (err) {
      setError(err.message)
    }
  }

  const isLoading = itemsLoading || suppliersLoading

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Record Shipment
          </DialogTitle>
          <DialogDescription>
            Record a new material delivery. The batch code will be generated automatically.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Main Fields Grid - 2 columns */}
            <div className="grid grid-cols-2 gap-4">
              {/* Row 1: Supplier (left) | Date (right) */}
              <div className="space-y-2">
                <Label htmlFor="supplier">Supplier</Label>
                <Select
                  value={formData.supplier_id}
                  onValueChange={(value) => {
                    if (value === '__create_new__') {
                      setSupplierDialogOpen(true)
                    } else {
                      handleChange('supplier_id', value)
                    }
                  }}
                  disabled={isLoading}
                >
                  <SelectTrigger id="supplier">
                    <SelectValue placeholder="Select supplier..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__create_new__" className="text-primary font-medium">
                      <span className="flex items-center gap-1">
                        <Plus className="h-3.5 w-3.5" />
                        Create New Supplier
                      </span>
                    </SelectItem>
                    {suppliers.map((supplier) => (
                      <SelectItem key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedItem?.preferred_supplier_id && formData.supplier_id === selectedItem.preferred_supplier_id && (
                  <p className="text-xs text-muted-foreground">Default supplier</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="purchase_date" className="flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5" />
                  Date
                </Label>
                <Input
                  id="purchase_date"
                  type="date"
                  value={formData.purchase_date}
                  onChange={(e) => handleChange('purchase_date', e.target.value)}
                />
              </div>

              {/* Row 2: Item | Quantity */}
              <div className="space-y-2">
                <Label htmlFor="item">Item *</Label>
                <Select
                  value={formData.item_id}
                  onValueChange={(value) => {
                    if (value === '__create_new__') {
                      setItemDialogOpen(true)
                    } else {
                      handleChange('item_id', value)
                    }
                  }}
                  disabled={isLoading || !!defaultItem}
                >
                  <SelectTrigger id="item">
                    <SelectValue placeholder="Select item..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__create_new__" className="text-primary font-medium">
                      <span className="flex items-center gap-1">
                        <Plus className="h-3.5 w-3.5" />
                        Create New Item
                      </span>
                    </SelectItem>
                    {inventoryItems.map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.name} ({item.batch_prefix})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="quantity">
                  Qty {selectedItem?.purchase_unit ? `(${selectedItem.purchase_unit}s)` : ''} *
                </Label>
                <Input
                  id="quantity"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="e.g., 1"
                  value={formData.quantity}
                  onChange={(e) => handleChange('quantity', e.target.value)}
                />
              </div>

              {/* Row 3: Product Cost | Delivery Cost */}
              <div className="space-y-2">
                <Label htmlFor="product_cost">Product Cost *</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">£</span>
                  <Input
                    id="product_cost"
                    type="number"
                    step="0.01"
                    min="0"
                    className="pl-7"
                    placeholder="0.00"
                    value={formData.product_cost}
                    onChange={(e) => handleChange('product_cost', e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="delivery_cost">Delivery Cost</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">£</span>
                  <Input
                    id="delivery_cost"
                    type="number"
                    step="0.01"
                    min="0"
                    className="pl-7"
                    placeholder="0.00"
                    value={formData.delivery_cost}
                    onChange={(e) => handleChange('delivery_cost', e.target.value)}
                  />
                </div>
              </div>

              {/* Row 4: VAT | Invoice Reference */}
              <div className="space-y-2">
                <Label htmlFor="vat_amount">VAT</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">£</span>
                  <Input
                    id="vat_amount"
                    type="number"
                    step="0.01"
                    min="0"
                    className="pl-7"
                    placeholder="0.00"
                    value={formData.vat_amount}
                    onChange={(e) => handleChange('vat_amount', e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="invoice_reference">Invoice Ref</Label>
                <Input
                  id="invoice_reference"
                  placeholder="e.g., INV-2024-001"
                  value={formData.invoice_reference}
                  onChange={(e) => handleChange('invoice_reference', e.target.value)}
                />
              </div>
            </div>

            {/* Conversion preview - full width below grid */}
            {selectedItem && formData.quantity && selectedItem.conversion_factor !== 1 && (
              <p className="text-sm text-muted-foreground">
                = {formatNumber(calculations.calculatedQuantity, 2)} {selectedItem.unit_of_measure} (based on {selectedItem.conversion_factor} {selectedItem.unit_of_measure}/{selectedItem.purchase_unit})
              </p>
            )}

            {/* Quantity Override Option */}
            <div className="flex items-center gap-2">
              <Checkbox
                id="use_quantity_override"
                checked={formData.use_quantity_override}
                onCheckedChange={(checked) => handleChange('use_quantity_override', checked)}
              />
              <Label htmlFor="use_quantity_override" className="text-sm font-normal cursor-pointer">
                Override with actual {selectedItem?.unit_of_measure || 'quantity'} received
              </Label>
            </div>

            {formData.use_quantity_override && (
              <div className="pl-6 space-y-2">
                <Label htmlFor="quantity_override">
                  Actual {selectedItem?.unit_of_measure || 'quantity'} received *
                </Label>
                <Input
                  id="quantity_override"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder={`e.g., ${selectedItem?.conversion_factor ? Math.round(selectedItem.conversion_factor * 1.01) : '1000'}`}
                  value={formData.quantity_override}
                  onChange={(e) => handleChange('quantity_override', e.target.value)}
                />
              </div>
            )}

            {/* Calculations Preview */}
            {selectedItem && formData.quantity && formData.product_cost && (
              <Card className="bg-muted/50">
                <CardContent className="p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Calculator className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Cost Calculation</span>
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                    <div className="text-muted-foreground">Product cost:</div>
                    <div>{formatCurrency(calculations.productCost)}</div>

                    <div className="text-muted-foreground">Delivery cost:</div>
                    <div>{formatCurrency(calculations.deliveryCost)}</div>

                    {calculations.vatAmount > 0 && (
                      <>
                        <div className="text-muted-foreground">VAT:</div>
                        <div>{formatCurrency(calculations.vatAmount)}</div>
                      </>
                    )}

                    <Separator className="col-span-2 my-1" />

                    <div className="text-muted-foreground font-medium">Landed cost:</div>
                    <div className="font-medium">{formatCurrency(calculations.landedCost)}</div>

                    {calculations.vatAmount > 0 && (
                      <>
                        <div className="text-muted-foreground">Landed cost ex VAT:</div>
                        <div>{formatCurrency(calculations.landedCostExVat)}</div>
                      </>
                    )}

                    <div className="text-muted-foreground">Quantity:</div>
                    <div>
                      {formatNumber(calculations.effectiveQuantity, 2)} {selectedItem.unit_of_measure}
                      {calculations.quantityOverridden && (
                        <span className="text-xs text-amber-600 ml-1">(overridden)</span>
                      )}
                    </div>

                    <Separator className="col-span-2 my-1" />

                    <div className="text-muted-foreground font-medium">Unit cost:</div>
                    <div className="font-medium">
                      {formatCurrency(calculations.unitCost)}/{selectedItem.unit_of_measure}
                    </div>

                    {calculations.vatAmount > 0 && (
                      <>
                        <div className="text-muted-foreground font-medium text-primary">Price/{selectedItem.unit_of_measure} ex VAT:</div>
                        <div className="font-medium text-primary">
                          {formatCurrency(calculations.unitCostExVat)}
                        </div>
                      </>
                    )}

                    <div className="text-muted-foreground">Batch code:</div>
                    <div className="font-mono text-xs">{batchCodePreview}</div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Notes - full width at bottom */}
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                placeholder="Additional notes about this shipment..."
                rows={2}
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || isLoading}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Recording...
                </>
              ) : (
                'Record Shipment'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>

      {/* Inline Creation Dialogs */}
      <SupplierDialog
        open={supplierDialogOpen}
        onOpenChange={setSupplierDialogOpen}
        supplier={null}
        onSuccess={handleSupplierCreated}
      />

      <InventoryItemDialog
        open={itemDialogOpen}
        onOpenChange={setItemDialogOpen}
        item={null}
        onSuccess={handleItemCreated}
      />
    </Dialog>
  )
}
